/**
 * Responsibility: show the working, so a finding can be disagreed with.
 *
 * **Check the dashed boundary first.** Everything is measured relative to
 * where the card was decided to be, so if that outline is wrong then every
 * number inside it is wrong too - and it will still look like a confident
 * result. Four of five failed attempts at measuring whitening failed here
 * rather than in the measurement itself.
 *
 * The corner sheet is the second thing to check. A corner defect is one or two
 * millimetres across and invisible at page scale, so the four corners are cut
 * out, magnified and put side by side, where three clean corners and one soft
 * one is obvious at a glance.
 */

import { Alert, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import type { ScanResult } from '../../api/client';

export function VerifyPanel({ result }: { result: ScanResult }) {
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">What the analysis decided</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              size="small"
              label={result.detected ? 'card found' : result.detect_reason}
              color={result.detected ? 'success' : 'error'}
            />
            <Chip size="small" label={`${result.px_per_mm} px/mm`} />
            <Chip
              size="small"
              variant="outlined"
              label={`border band ${result.band_depth_mm} mm`}
            />
            <Chip size="small" variant="outlined" label={`${result.findings.length} findings`} />
          </Stack>

          {result.centering.measurable ? (
            <Typography variant="body2">
              Centering &mdash; L {result.centering.left_mm} / R {result.centering.right_mm} mm (
              {result.centering.left_right.toFixed(0)}%), T {result.centering.top_mm} / B{' '}
              {result.centering.bottom_mm} mm ({result.centering.top_bottom.toFixed(0)}%) &mdash;{' '}
              {result.centering.grade}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Centering not measurable: {result.centering.reason}
            </Typography>
          )}

          <Alert severity="info" icon={false}>
            Check the green dashed outline before anything else. If it is not on the
            card edge, every measurement inside it is measuring the background.
          </Alert>
        </Stack>
      </Paper>

      {result.overlay_png_b64 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Detected boundary and flagged marks
          </Typography>
          <Box
            component="img"
            src={`data:image/png;base64,${result.overlay_png_b64}`}
            alt="Detected card boundary with any findings ringed"
            sx={{ width: '100%', borderRadius: 1 }}
          />
        </Paper>
      )}

      {result.corners_png_b64 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Corners, magnified
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top left and right on the upper row, bottom left and right below. Anything
            outside the card is blanked: the pale crescent beyond a rounded corner is
            background, and it is the brightest thing in the crop.
          </Typography>
          <Box
            component="img"
            src={`data:image/png;base64,${result.corners_png_b64}`}
            alt="The four card corners magnified side by side"
            sx={{ width: '100%', borderRadius: 1, mt: 1 }}
          />
        </Paper>
      )}

      {result.findings.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle2">Findings in detail</Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={0.75}>
            {result.findings.map((f, i) => (
              <Typography key={`${f.region}-${i}`} variant="body2">
                <strong>{f.region.replace(/_/g, ' ')}</strong> &mdash; {f.area_mm2} mm2,{' '}
                {f.delta} L* lighter than the border around it, {f.severity}, confidence{' '}
                {f.confidence}
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
